import React, { useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";

import QRCode from "qrcode";
import {
  FileEarmarkText,
  Tablet,
  QrCodeScan,
  ArrowClockwise,
  Clipboard,
  InfoCircle,
  Printer,
  Stack,
} from "react-bootstrap-icons";
import { useCircleDarkMode } from "../hooks/useCircleDarkMode";
import { retrieveTemplate } from "../components/printing/retrieveTemplate";

const PalletLabelInboundPage = () => {
  const loadRefInputRef = useRef<HTMLInputElement>(null);
  const labelCountInputRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | undefined>();
  const [generatedIds, setGeneratedIds] = useState<string[]>([]);
  const [loadReference, setLoadReference] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [qrCodeDataUrls, setQrCodeDataUrls] = useState<string[]>([]);
  const [isGeneratingContent, setIsGeneratingContent] =
    useState<boolean>(false);
  const [showPrintPreview, setShowPrintPreview] = useState<boolean>(false);
  const [selectedLabelIndex, setSelectedLabelIndex] = useState<number>(0);

  const darkMode = useCircleDarkMode();

  const generateQRCode = async (text: string): Promise<string> => {
    try {
      const dataUrl = await QRCode.toDataURL(text, {
        errorCorrectionLevel: "M",
        width: 800, // Large size for printing
        margin: 1,
      });
      return dataUrl;
    } catch (error) {
      console.error("Error generating QR code:", error);
      setError("Failed to generate QR code. Please try again.");
      return "";
    }
  };

  const handleGenerateIds = async () => {
    const loadReferenceNumber = loadRefInputRef.current?.value || "";
    const labelCount = parseInt(labelCountInputRef.current?.value || "0", 10);

    if (!loadReferenceNumber) {
      setError("Please enter a Load Reference Number");
      return;
    }

    if (!labelCount || labelCount <= 0 || labelCount > 100) {
      setError("Please enter a valid number of labels (1-100)");
      return;
    }

    setError(undefined);
    setIsGeneratingContent(true);

    try {
      // Generate IDs for each label
      const currentDate = new Date();
      const day = String(currentDate.getDate()).padStart(2, "0");
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");

      const ids: string[] = [];
      const qrCodes: string[] = [];

      for (let i = 1; i <= labelCount; i++) {
        const palletNumber = `P${String(i).padStart(3, "0")}`;
        const id = `${loadReferenceNumber}-${palletNumber}-${day}-${month}`;
        ids.push(id);

        // Generate QR code for this ID
        const qrCode = await generateQRCode(id);
        qrCodes.push(qrCode);
      }

      setGeneratedIds(ids);
      setQrCodeDataUrls(qrCodes);
      setLoadReference(loadReferenceNumber);
      setSelectedLabelIndex(0);

      // Show preview
      setShowPreview(true);
    } catch (error) {
      console.error("Error in generation process:", error);
      setError("An error occurred during generation. Please try again.");
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handlePrint = async () => {
    setShowPrintPreview(true);
    setError(undefined);

    try {
      const generatedDate = new Date().toLocaleDateString();
      const currentId = generatedIds[selectedLabelIndex];
      const currentQrCode = qrCodeDataUrls[selectedLabelIndex];
      const palletNumber = currentId.split("-")[1]; // Extract the pallet number (P001, P002, etc.)

      let templateHtml;
      try {
        templateHtml = await retrieveTemplate("/templates/pallet-label-a4.hbs");
      } catch (error) {
        throw new Error("Could not load label template. Please try again.");
      }

      // Replace template variables with actual values
      const processedHtml = templateHtml
        .replace(/{{generatedId}}/g, currentId)
        .replace(/{{qrCodeDataUrl}}/g, currentQrCode)
        .replace(/{{loadReferenceNumber}}/g, loadReference)
        .replace(/{{palletNumber}}/g, palletNumber)
        .replace(/{{generatedDate}}/g, generatedDate);

      const printWindow = window.open("", "_blank");

      if (printWindow) {
        const printDocument = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pallet Label - ${currentId}</title>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 0;
              size: A4;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .label-container {
              page-break-inside: avoid;
              break-inside: avoid;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="label-container">${processedHtml}</div>
          <script>
            // Automatically print when loaded
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

        printWindow.document.open();
        printWindow.document.write(printDocument);
        printWindow.document.close();

        // Close the preview modal after a short delay
        setTimeout(() => {
          setShowPrintPreview(false);
        }, 500);
      } else {
        setError(
          "Could not open print window. Please check your popup blocker settings."
        );
        setShowPrintPreview(false);
      }
    } catch (error) {
      console.error("Error in print process:", error);
      setError(`Failed to generate print preview`);
      setShowPrintPreview(false);
    }
  };

  const handlePrintAll = async () => {
    setShowPrintPreview(true);
    setError(undefined);

    try {
      const generatedDate = new Date().toLocaleDateString();

      let templateHtml;
      try {
        templateHtml = await retrieveTemplate("/templates/pallet-label-a4.hbs");
      } catch (error) {
        throw new Error("Could not load label template. Please try again.");
      }

      // Create HTML content for all labels
      const labelsHtmlArray: string[] = [];

      for (let i = 0; i < generatedIds.length; i++) {
        const currentId = generatedIds[i];
        const currentQrCode = qrCodeDataUrls[i];
        const palletNumber = currentId.split("-")[1]; // Extract pallet number

        // Process template for this label
        const processedHtml = templateHtml
          .replace(/{{generatedId}}/g, currentId)
          .replace(/{{qrCodeDataUrl}}/g, currentQrCode)
          .replace(/{{loadReferenceNumber}}/g, loadReference)
          .replace(/{{palletNumber}}/g, palletNumber)
          .replace(/{{generatedDate}}/g, generatedDate);

        labelsHtmlArray.push(processedHtml);
      }

      const printWindow = window.open("", "_blank");

      if (printWindow) {
        const printDocument = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pallet Labels - ${loadReference}</title>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 0;
              size: A4;
            }
            body {
              margin: 0;
              padding: 0;
              counter-reset: page;
            }
            .page {
              width: 100%;
              height: 100vh;
              page-break-after: always;
              break-after: page;
              position: relative;
            }
            .page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            /* Modern CSS for print */
            @media print {
              body {
                width: 100%;
                height: 100%;
              }
              .page {
                page-break-before: always;
                break-before: page;
                page-break-after: always;
                break-after: page;
                page-break-inside: avoid;
                break-inside: avoid;
              }
              .page:first-child {
                page-break-before: auto;
                break-before: auto;
              }
              .page:last-child {
                page-break-after: auto;
                break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtmlArray
            .map(
              (html, index) => `
            <div class="page" id="page-${index + 1}">
              ${html}
            </div>
          `
            )
            .join("")}
          <script>
            // Automatically print when loaded
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

        printWindow.document.open();
        printWindow.document.write(printDocument);
        printWindow.document.close();

        // Close the preview modal after a short delay
        setTimeout(() => {
          setShowPrintPreview(false);
        }, 500);
      } else {
        setError(
          "Could not open print window. Please check your popup blocker settings."
        );
        setShowPrintPreview(false);
      }
    } catch (error) {
      console.error("Error in print all process:", error);
      setError(`Failed to generate print preview for all labels`);
      setShowPrintPreview(false);
    }
  };

  const resetForm = () => {
    if (loadRefInputRef.current) loadRefInputRef.current.value = "";
    if (labelCountInputRef.current) labelCountInputRef.current.value = "";
    setGeneratedIds([]);
    setLoadReference("");
    setQrCodeDataUrls([]);
    setShowPreview(false);
  };

  const copyToClipboard = () => {
    const currentId = generatedIds[selectedLabelIndex];
    navigator.clipboard.writeText(currentId);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Container fluid className="p-0">
      {/* Header with help toggle */}
      <div
        className={`py-4 mb-4 ${
          darkMode ? "bg-dark text-white" : "bg-gradient text-black"
        }`}
      >
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-1">
              <QrCodeScan className="me-2 mb-1" />
              Pallet Label Generator
            </h2>
            <Button
              variant={darkMode ? "outline-light" : "light"}
              size="sm"
              onClick={() => setShowInstructions(!showInstructions)}
              className={`d-flex align-items-center ${
                darkMode ? "bg-dark text-white" : "bg-gradient text-black"
              }`}
            >
              <InfoCircle className="me-2" />
              {showInstructions ? "Hide Help" : "Need Help?"}
            </Button>
          </div>
          <p
            className={`mb-0 ${
              darkMode ? "bg-dark text-white" : "bg-gradient text-black"
            }`}
          >
            Generate and print pallet identification labels with QR codes
          </p>
        </Container>
      </div>

      <Container>
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(undefined)}
          >
            <Alert.Heading>Error</Alert.Heading>
            {error}
          </Alert>
        )}

        {/* Navigation buttons when viewing preview */}
        {showPreview && (
          <div className="mb-3">
            <Button
              variant="outline-secondary"
              onClick={() => setShowPreview(false)}
            >
              Back to Generator
            </Button>
          </div>
        )}

        {/* Main content area */}
        {!showPreview ? (
          <Row>
            <Col xs={12}>
              {/* Inline guided help - only shown when help is toggled on */}
              {showInstructions && (
                <Card
                  className={`border-0 shadow-sm mb-4 ${
                    darkMode ? "bg-dark text-white" : "bg-light"
                  }`}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex">
                      <div className="me-3 text-primary">
                        <InfoCircle size={24} />
                      </div>
                      <div>
                        <h5 className="mb-2">How to Generate Pallet Labels</h5>
                        <ol className="mb-0 ps-3">
                          <li className="mb-2">
                            Enter the <strong>Load Reference Number</strong> for
                            this shipment
                          </li>
                          <li className="mb-2">
                            Enter the <strong>Number of Labels</strong> you need
                            to generate
                          </li>
                          <li className="mb-2">
                            Click <strong>Generate Pallet IDs</strong> to create
                            unique identifiers
                          </li>
                          <li className="mb-2">
                            Review the generated QR codes and information
                          </li>
                          <li>
                            Click <strong>Print Label</strong> to print the
                            selected pallet label or <strong>Print All</strong>{" "}
                            to print all labels at once
                          </li>
                        </ol>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Input Form */}
              <Card className="border-0 shadow">
                <Card.Body className="p-4">
                  <h4 className="mb-4">Enter Pallet Information</h4>
                  <Form>
                    <Row>
                      <Col sm={6}>
                        <Form.Group
                          className="mb-4"
                          controlId="loadReferenceNumber"
                        >
                          <Form.Label>
                            <strong>Load Reference Number</strong>
                            <span className="text-danger ms-1">*</span>
                            {/* Inline help icon with tooltip */}
                            {!showInstructions && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-load-ref">
                                    Enter the unique reference number for this
                                    load
                                  </Tooltip>
                                }
                              >
                                <InfoCircle
                                  className="ms-2 text-muted"
                                  style={{ cursor: "help" }}
                                />
                              </OverlayTrigger>
                            )}
                          </Form.Label>
                          <InputGroup>
                            <InputGroup.Text className="bg-light">
                              <FileEarmarkText />
                            </InputGroup.Text>
                            <Form.Control
                              ref={loadRefInputRef}
                              type="text"
                              placeholder="e.g. LRN12345"
                              defaultValue=""
                              className="py-2"
                            />
                          </InputGroup>
                          <Form.Text muted>
                            Enter the unique reference number for this load
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col sm={6}>
                        <Form.Group className="mb-4" controlId="labelCount">
                          <Form.Label>
                            <strong>Number of Labels</strong>
                            <span className="text-danger ms-1">*</span>
                            {/* Inline help icon with tooltip */}
                            {!showInstructions && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-label-count">
                                    Enter how many labels you need to generate
                                    (1-100)
                                  </Tooltip>
                                }
                              >
                                <InfoCircle
                                  className="ms-2 text-muted"
                                  style={{ cursor: "help" }}
                                />
                              </OverlayTrigger>
                            )}
                          </Form.Label>
                          <InputGroup>
                            <InputGroup.Text className="bg-light">
                              <Tablet />
                            </InputGroup.Text>
                            <Form.Control
                              ref={labelCountInputRef}
                              type="number"
                              min="1"
                              max="100"
                              placeholder="e.g. 5"
                              defaultValue=""
                              className="py-2"
                            />
                          </InputGroup>
                          <Form.Text muted>
                            Enter how many labels you need to generate (1-100)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-grid gap-2 d-md-flex mt-4">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleGenerateIds}
                        className="px-4 py-2"
                        disabled={isGeneratingContent}
                      >
                        {isGeneratingContent ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Generating...
                          </>
                        ) : (
                          "Generate Pallet IDs"
                        )}
                      </Button>

                      <Button
                        variant="outline-secondary"
                        onClick={resetForm}
                        className="px-4 py-2"
                        disabled={isGeneratingContent}
                      >
                        <ArrowClockwise className="me-2" /> Reset
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <Card className="border-0 shadow">
            <Card.Header
              className={`${
                darkMode ? "bg-dark text-white" : "bg-primary text-white"
              } py-3`}
            >
              <h4 className="mb-0">
                Generated Pallet Labels ({generatedIds.length})
              </h4>
            </Card.Header>
            <Card.Body className="p-4">
              <Row>
                <Col md={4} className="mb-4">
                  <div className="mb-3">
                    <h5>Select a Pallet Label</h5>
                  </div>
                  <ListGroup>
                    {generatedIds.map((id, index) => (
                      <ListGroup.Item
                        key={id}
                        action
                        active={index === selectedLabelIndex}
                        onClick={() => setSelectedLabelIndex(index)}
                      >
                        {id.split("-")[1]} {/* Show P001, P002, etc. */}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Col>
                <Col md={8}>
                  <Row>
                    <Col lg={7}>
                      <div className="mb-4">
                        <h5>Pallet Identifier</h5>
                        <div className="d-flex align-items-center">
                          <div className="bg-light p-3 rounded flex-grow-1">
                            <code className="fs-4">
                              {generatedIds[selectedLabelIndex]}
                            </code>
                          </div>
                          <Button
                            variant="outline-secondary"
                            className="ms-2"
                            onClick={copyToClipboard}
                          >
                            <Clipboard /> {isCopied ? "Copied!" : "Copy"}
                          </Button>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h5>Components</h5>
                        <div>
                          <Badge bg="primary" className="p-2 me-2 mb-2">
                            Load Ref: {loadReference}
                          </Badge>
                          <Badge bg="info" className="p-2 me-2 mb-2">
                            Pallet:{" "}
                            {generatedIds[selectedLabelIndex]?.split("-")[1] ||
                              ""}
                          </Badge>
                          <Badge bg="secondary" className="p-2 mb-2">
                            Date: {new Date().toLocaleDateString()}
                          </Badge>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">Print Options</h5>
                          <div className="d-flex">
                            <Button
                              variant="success"
                              className="d-flex align-items-center me-2"
                              onClick={handlePrint}
                              disabled={
                                !qrCodeDataUrls.length ||
                                isGeneratingContent ||
                                showPrintPreview
                              }
                            >
                              <Printer className="me-2" /> Print Label
                            </Button>
                            <Button
                              variant="primary"
                              className="d-flex align-items-center"
                              onClick={handlePrintAll}
                              disabled={
                                !qrCodeDataUrls.length ||
                                isGeneratingContent ||
                                showPrintPreview
                              }
                            >
                              <Stack className="me-2" /> Print All
                            </Button>
                          </div>
                        </div>
                        <p className="text-muted small mt-2">
                          Print the selected label or all labels at once
                        </p>
                      </div>
                    </Col>

                    <Col lg={5} className="text-center">
                      <div className="mb-3">
                        <h5>QR Code</h5>
                      </div>
                      <div className="d-inline-block bg-white p-4 rounded border">
                        {qrCodeDataUrls[selectedLabelIndex] ? (
                          <img
                            src={qrCodeDataUrls[selectedLabelIndex]}
                            alt="QR Code"
                            width={200}
                            height={200}
                            className="d-block mx-auto"
                          />
                        ) : (
                          <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ width: 200, height: 200 }}
                          >
                            <p>Generating QR code...</p>
                          </div>
                        )}
                      </div>
                      <p className="text-muted mt-3 small">
                        Scan this QR code to access pallet information
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Print Preview Modal */}
      <Modal show={showPrintPreview} centered size="sm">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 mb-0">Opening print preview...</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PalletLabelInboundPage;
